/**
 * @fileoverview Year-view day cell — small clickable day box that
 *               navigates to the day view via context.
 */

import { isToday } from "date-fns";

import { useCalendar } from "@/calendar/contexts/use-calendar";

import { cn } from "@/utils/misc";

import type { IEvent } from "@/calendar/interfaces";
import type { TEventColor } from "@/calendar/types";

interface IProps {
  day: number;
  date: Date;
  events: IEvent[];
}

const COLOR_DOT_CLASS: Record<TEventColor, string> = {
  blue: "bg-blue-600",
  green: "bg-green-600",
  red: "bg-red-600",
  yellow: "bg-yellow-600",
  purple: "bg-purple-600",
  orange: "bg-orange-600",
  gray: "bg-neutral-600",
  pink: "bg-pink-600",
};

export function YearViewDayCell({ day, date, events }: IProps) {
  const { setSelectedDate, setView } = useCalendar();

  const maxIndicators = 3;
  const eventCount = events.length;

  const handleClick = () => {
    setSelectedDate(date);
    setView("day");
  };

  return (
    <button
      onClick={handleClick}
      type="button"
      className="flex h-11 flex-1 flex-col items-center justify-start gap-0.5 rounded-md pt-1 hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <div
        className={cn(
          "flex size-6 items-center justify-center rounded-full text-xs font-medium",
          isToday(date) && "bg-primary font-semibold text-primary-foreground",
        )}
      >
        {day}
      </div>

      {eventCount > 0 && (
        <div className="mt-0.5 flex gap-0.5">
          {eventCount <= maxIndicators ? (
            events.map((event) => (
              <div
                key={event.id}
                className={cn(
                  "size-1.5 rounded-full",
                  COLOR_DOT_CLASS[event.color],
                )}
              />
            ))
          ) : (
            <>
              <div
                className={cn(
                  "size-1.5 rounded-full",
                  COLOR_DOT_CLASS[events[0].color],
                )}
              />
              <span className="text-[7px] text-muted-foreground">
                +{eventCount - 1}
              </span>
            </>
          )}
        </div>
      )}
    </button>
  );
}
